import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Stack, SxProps } from '@mui/material';
import {
  PointOfInterest,
  ImageType,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  useRemoveAttachmentsFromPoiMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  SelectedPointOfInterestDocument,
} from '@/__generated__/graphql';
import { usePoiPostInspectionNoteMutation } from '@/hooks/usePoiPostInspectionNoteMutation';
import { useHavePermission } from '@/hooks';
import { usePoiAttachments } from '@/hooks/usePoiAttachments';
import {
  poiAttachmentsAtom,
  postInspectionImagesAtom,
} from '@/components/Analysis/modules/pointOfInterest/state/atoms';
import { usePoiAttachmentCaption } from '@/hooks/usePoiAttachmentCaption';
import { Permissions } from '@/types';
import { LocationTag, MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { Attachment } from '../../types';
import { AttachmentsViewer } from '../AttachmentsViewer';
import validateFiles from '../ValidateFiles';
import { PostInspectionNotesEditor } from './PostInspectionNotesEditor';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { MediaWrapperContainer, MediaSectionTitle, UploadButton, UploadButtonText } from './styles';

type Props = {
  selectedPoiInfo: PointOfInterest;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode?: boolean;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  readOnly?: boolean;
  fallbackText?: string;
  sx?: SxProps;
};

export const PostInspectionMedia = ({
  selectedPoiInfo,
  isUpdating: externalIsUpdating,
  setIsUpdating: externalSetIsUpdating,
  isEditMode = false,
  readOnly = true,
  fallbackText,
  saveRef,
  sx,
}: Props) => {
  const { updateNote } = usePoiPostInspectionNoteMutation();
  const userCanUploadMedia = useHavePermission(Permissions.CREATE_UPLOAD_MEDIA);
  const [internalIsUpdating, setInternalIsUpdating] = useState(false);
  const isUpdating = externalIsUpdating ?? internalIsUpdating;
  const setIsUpdating = externalSetIsUpdating ?? setInternalIsUpdating;
  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const [removeAttachmentsFromPoi] = useRemoveAttachmentsFromPoiMutation();
  const { updatePoiAttachmentCaption } = usePoiAttachmentCaption();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingCaptionChanges = useRef(new Map<string, string>());

  usePoiAttachments({
    selectedPoiInfo,
    isUpdating,
  });

  const postInspectionImages = useAtomValue(postInspectionImagesAtom);
  const setPoiAttachments = useSetAtom(poiAttachmentsAtom);

  const initialNote = selectedPoiInfo.postInspectionNote ?? '';
  const [pendingNote, setPendingNote] = useState<string | null | undefined>(null);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [pendingNewAttachments, setPendingNewAttachments] = useState<Attachment[]>([]);
  const [pendingRemovalIds, setPendingRemovalIds] = useState<string[]>([]);

  const handleCaptionChange = useCallback((attachmentId: string, caption: string) => {
    pendingCaptionChanges.current.set(attachmentId, caption);
  }, []);

  const onRemoveAttachment = useCallback((attachmentIds: string[]) => {
    setPendingRemovalIds((prev) => [...prev, ...attachmentIds]);
  }, []);

  const visibleAttachments = postInspectionImages.filter(
    (a) => !a.id || !pendingRemovalIds.includes(a.id)
  );

  const postInspectionImagesUpload = useCallback(
    async (filesToUpload: Attachment[]) => {
      setIsUpdating(true);
      setPoiAttachments((prev) => [...prev, ...filesToUpload]);
      try {
        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              files: filesToUpload.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                imageType: ImageType.PostInspection,
              })),
              lastModifiedFor: 'Media Upload',
            },
          },
          refetchQueries: [SelectedPointOfInterestDocument],
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setPoiAttachments((prev) =>
              prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
            );
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                };
              }
              return attachment;
            })
          );
        }
        showSnackBar('Files uploaded successfully', 'success');
      } catch (error) {
        console.error('Error uploading files:', error);
        showSnackBar('Failed to upload files', 'error');
        setPoiAttachments((prev) =>
          prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
        );
        setIsUpdating(false);
      }
    },
    [selectedPoiInfo.id, uploadAttachmentsToPoi, showSnackBar, setIsUpdating, setPoiAttachments]
  );

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      const totalFiles =
        postInspectionImages.length + pendingNewAttachments.length + (files?.length || 0);
      if (totalFiles > MAX_FILE_UPLOAD_LIMIT) {
        showSnackBar(
          `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
          'error'
        );
        return;
      }
      if (files && files.length > 0) {
        const fileArray = [...files];
        const { validFiles, fileErrors } = validateFiles(fileArray);
        if (fileErrors.length > 0) {
          fileErrors.forEach((error) => showSnackBar(error, 'error'));
          return;
        }

        const allExisting = [...postInspectionImages, ...pendingNewAttachments];
        const duplicateFiles = validFiles.filter((file) =>
          allExisting.some((att) => att.name === file.name.replace(/[^\w.-]/g, '_'))
        );
        if (duplicateFiles.length > 0) {
          showSnackBar(
            `Duplicate file names found: ${duplicateFiles.map((f) => f.name).join(', ')}`,
            'error'
          );
          return;
        }

        const newAttachments: Attachment[] = await Promise.all(
          validFiles.map(async (file, index) => {
            const isImageFile = file.type.startsWith('image/');
            const base64 = isImageFile
              ? await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.addEventListener('load', () => {
                    resolve(typeof reader.result === 'string' ? reader.result : '');
                  });
                  reader.addEventListener('error', () => reject(reader.error));
                })
              : undefined;
            return {
              uuid: `file-${Date.now()}-${index}`,
              name: file.name,
              type: file.type,
              base64,
              isNew: true,
              size: file.size,
              file,
              uploadStatus: FileUploadStatus.Inprogress,
              imageType: ImageType.PostInspection,
            };
          })
        );
        if (isEditMode) {
          setPendingNewAttachments((prev) => [...prev, ...newAttachments]);
        } else {
          await postInspectionImagesUpload(newAttachments);
        }
      }
      event.target.value = '';
    },
    [
      postInspectionImages,
      pendingNewAttachments,
      showSnackBar,
      isEditMode,
      postInspectionImagesUpload,
    ]
  );

  const handleRemovePendingAttachment = useCallback((index: number) => {
    setPendingNewAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setIsUpdating(true);
    try {
      // Save pending caption changes
      const captionEntries = [...pendingCaptionChanges.current.entries()];
      for (const [attachmentId, caption] of captionEntries) {
        const attachment = postInspectionImages.find(
          (a) => a.id === attachmentId || a.uuid === attachmentId
        );
        if (attachment?.id) {
          await updatePoiAttachmentCaption(
            selectedPoiInfo.id,
            attachment.uuid,
            attachment.id,
            caption
          );
          setPoiAttachments((prev) =>
            prev.map((a) => (a.id === attachment.id ? { ...a, caption } : a))
          );
        }
      }
      pendingCaptionChanges.current.clear();

      // Remove pending attachments
      if (pendingRemovalIds.length > 0) {
        for (const fileId of pendingRemovalIds) {
          await removeAttachmentsFromPoi({
            variables: {
              input: {
                pointOfInterestId: selectedPoiInfo.id,
                fileIds: [fileId],
              },
            },
            refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
          });
        }
        setPoiAttachments((prev) => prev.filter((a) => !a.id || !pendingRemovalIds.includes(a.id)));
        setPendingRemovalIds([]);
      }

      // Save note if changed (when no new attachments to upload)
      if (pendingNote !== initialNote && pendingNewAttachments.length === 0) {
        await updateNote(pendingNote, selectedPoiInfo.id);
      }

      if (pendingNewAttachments.length > 0) {
        // Add to atom so AttachmentsViewer can render them and trigger S3 upload
        setPoiAttachments((prev) => [...prev, ...pendingNewAttachments]);
        // Clear pending list immediately so they don't show in both places
        setPendingNewAttachments([]);

        const notePayload = pendingNote !== initialNote ? { postInspectionNote: pendingNote } : {};

        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              ...notePayload,
              files: pendingNewAttachments.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                imageType: ImageType.PostInspection,
              })),
              lastModifiedFor: 'Media Upload',
            },
          },
          refetchQueries: [SelectedPointOfInterestDocument],
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setPoiAttachments((prev) =>
              prev.filter((a) => !pendingNewAttachments.some((p) => p.uuid === a.uuid))
            );
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                };
              }
              return attachment;
            })
          );
        }
      } else {
        setIsUpdating(false);
      }

      showSnackBar('Changes saved successfully', 'success');
    } catch (error) {
      console.error('Error saving changes:', error);
      showSnackBar('Failed to save changes', 'error');
      setIsUpdating(false);
    } finally {
      setIsNoteEditing(false);
    }
  }, [
    pendingNote,
    initialNote,
    pendingNewAttachments,
    pendingRemovalIds,
    postInspectionImages,
    selectedPoiInfo.id,
    updateNote,
    updatePoiAttachmentCaption,
    uploadAttachmentsToPoi,
    removeAttachmentsFromPoi,
    setIsUpdating,
    showSnackBar,
    setPoiAttachments,
  ]);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSave;
    }
  }, [saveRef, handleSave]);

  return (
    <MediaWrapperContainer sx={sx} className="mediaViewer">
      <MediaSectionTitle>Notes and attachments</MediaSectionTitle>

      <PostInspectionNotesEditor
        note={pendingNote ?? initialNote}
        onNoteChange={setPendingNote}
        isEditing={isNoteEditing}
        onEditingChange={(editing) => {
          setIsNoteEditing(editing);
        }}
        canEdit={isEditMode && userCanUploadMedia}
      />

      {isEditMode && (
        <input
          ref={fileInputRef}
          accept="image/jpeg,image/png,application/pdf,video/mp4"
          style={{ display: 'none' }}
          multiple
          type="file"
          onChange={handleFileSelect}
        />
      )}

      {isEditMode && userCanUploadMedia && (
        <UploadButton onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AbyssIcon iconName="load-view" style={{ width: '1.3rem', height: '1.2rem' }} />
            <UploadButtonText variant="button">Upload or drag file</UploadButtonText>
          </Stack>
        </UploadButton>
      )}

      {pendingNewAttachments.length > 0 &&
        [...pendingNewAttachments].reverse().map((attachment, displayIndex) => {
          const realIndex = pendingNewAttachments.length - 1 - displayIndex;
          return (
            <FileDisplay
              key={attachment.uuid || `pending-${realIndex}`}
              collectionName="poi"
              selectedAttachment={attachment}
              selectedAttachmentIndex={realIndex}
              handleRemoveAttachment={handleRemovePendingAttachment}
              showRemoveIcon
              location={LocationTag.ViewerSelectedPoiDetails}
              disableEnterSave={isEditMode}
              readOnly
              layout="list"
            />
          );
        })}

      <AttachmentsViewer
        attachments={[...visibleAttachments].reverse()}
        allAttachments={postInspectionImages}
        setPoiAttachments={setPoiAttachments}
        selectedPoiInfo={selectedPoiInfo}
        setIsUpdating={setIsUpdating}
        isUpdating={isUpdating}
        location={LocationTag.ViewerSelectedPoiDetails}
        layout="list"
        readOnly={readOnly || isUpdating}
        fallbackText={fallbackText}
        disableEnterSave={isEditMode}
        onCaptionChange={isEditMode ? handleCaptionChange : undefined}
        onRemoveAttachments={isEditMode ? onRemoveAttachment : undefined}
      />
    </MediaWrapperContainer>
  );
};
